import { forwardRef } from 'react';
import { Opportunity, OPPORTUNITY_STATUSES } from '@/hooks/useOpportunities';
import { OpportunityExportCard } from './OpportunityExportCard';

interface OpportunitiesKanbanExportProps {
  opportunities: Opportunity[];
}

const BASE_FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export const OpportunitiesKanbanExport = forwardRef<HTMLDivElement, OpportunitiesKanbanExportProps>(
  ({ opportunities }, ref) => {
    // Group by status
    const opportunitiesByStatus = OPPORTUNITY_STATUSES.reduce((acc, status) => {
      acc[status.id] = opportunities.filter(opp => opp.status === status.id);
      return acc;
    }, {} as Record<string, Opportunity[]>);

    return (
      <div 
        ref={ref}
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 0,
          width: '2100px',
          padding: '24px',
          backgroundColor: 'white',
          fontFamily: BASE_FONT,
          fontStyle: 'normal',
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: '20px' }}>
          <h1 style={{ 
            fontSize: '24px', 
            fontWeight: 700, 
            color: '#111827', 
            margin: 0,
            fontFamily: BASE_FONT,
            fontStyle: 'normal',
          }}>
            Opportunities Kanban
          </h1>
          <p style={{ 
            fontSize: '14px', 
            color: '#6b7280', 
            margin: '4px 0 0 0',
            fontFamily: BASE_FONT,
            fontStyle: 'normal',
          }}>
            {new Date().toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        {/* Kanban columns */}
        <div style={{ display: 'flex', gap: '16px' }}>
          {OPPORTUNITY_STATUSES.map(status => {
            const statusOpportunities = opportunitiesByStatus[status.id] || [];
            
            return (
              <div 
                key={status.id} 
                style={{ 
                  width: '330px', 
                  flexShrink: 0,
                  backgroundColor: '#f9fafb',
                  borderRadius: '12px',
                  padding: '12px',
                }}
              >
                {/* Column header */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  marginBottom: '12px',
                  paddingBottom: '8px',
                  borderBottom: '1px solid #e5e7eb',
                }}>
                  <div 
                    style={{ 
                      width: '12px', 
                      height: '12px', 
                      borderRadius: '50%', 
                      backgroundColor: status.color,
                      flexShrink: 0,
                    }} 
                  />
                  <span style={{ 
                    fontWeight: 600, 
                    fontSize: '14px', 
                    color: '#374151',
                    fontFamily: BASE_FONT,
                    fontStyle: 'normal',
                  }}>
                    {status.label}
                  </span>
                  <span style={{ 
                    backgroundColor: '#e5e7eb', 
                    padding: '2px 8px', 
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6b7280',
                    marginLeft: 'auto',
                    fontFamily: BASE_FONT,
                    fontStyle: 'normal',
                  }}>
                    {statusOpportunities.length}
                  </span>
                </div>

                {/* Cards */}
                <div style={{ minHeight: '100px' }}>
                  {statusOpportunities.map(opp => (
                    <OpportunityExportCard key={opp.id} opportunity={opp} />
                  ))}
                  {statusOpportunities.length === 0 && (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '24px 0', 
                      color: '#9ca3af',
                      fontSize: '13px',
                      fontFamily: BASE_FONT,
                      fontStyle: 'normal',
                    }}>
                      Geen opportunities
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ 
          marginTop: '16px', 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          color: '#9ca3af',
          fontSize: '11px',
          fontFamily: BASE_FONT,
          fontStyle: 'normal',
        }}>
          <span>Greenvision Energy</span>
          <span>Totaal: {opportunities.length} opportunities</span>
        </div>
      </div>
    );
  }
);

OpportunitiesKanbanExport.displayName = 'OpportunitiesKanbanExport';
